import { AuthState } from "@okta/okta-auth-js";
import { Action, Dispatch } from "redux";
import { fetchUpdates, IUpdateRequestParams } from "./Operations";
import { Action as AnyAction } from "@reduxjs/toolkit";
import { IFilter } from "../../Responses/duck/Models";
import * as moment from "moment";

export enum UpdateActionTypes {
  FetchUpdatesPending = "FETCH_UPDATES_PENDING",
  FetchedUpdates = "FETCHED_UPDATES",
  FetchUpdatesRejected = "FETCH_UPDATES_REJECTED",
  SetSortOrder = "SET_SORT_ORDER",
  ResetSortOrder = "RESET_SORT_ORDER",
  SetFilter = "SET_FILTER",
  ClearFilter = "CLEAR_FILTER",
  ClearAllFilters = "CLEAR_ALL_FILTERS"
}

export class FetchingUpdatesAction implements Action {
  public readonly type = UpdateActionTypes.FetchUpdatesPending;
}

export class FetchedUpdatesAction implements Action {
  public readonly type = UpdateActionTypes.FetchedUpdates;
  constructor(public payload: { updates: Api.IPagedResult<Api.INotificationViewModel> }) {  }
}

export class FetchUpdatesFailedAction implements Action {
  public readonly type = UpdateActionTypes.FetchUpdatesRejected;
  constructor(public payload: { error: string }) {  }
}

export class SetSortOrderAction implements Action {
  public readonly type = UpdateActionTypes.SetSortOrder;
  constructor(public payload: { orderBy: string }) {  }
}

export class ResetSortOrderAction implements Action {
  public readonly type = UpdateActionTypes.ResetSortOrder;
}

export class SetFilterAction implements Action {
  public readonly type = UpdateActionTypes.SetFilter;
  constructor(public payload: { field: string; value: any; operator?: string }) {  }
}

export class ClearFilterAction implements Action {
  public readonly type = UpdateActionTypes.ClearFilter;
  constructor(public payload: { field: string }) {  }
}

export class ClearAllFiltersAction implements Action {
  public readonly type = UpdateActionTypes.ClearAllFilters;
}

export type UpdateAction = FetchingUpdatesAction
  | FetchedUpdatesAction
  | FetchUpdatesFailedAction
  | SetSortOrderAction
  | ResetSortOrderAction
  | SetFilterAction
  | ClearFilterAction
  | ClearAllFiltersAction;

export const getUpdateListThunk: (auth: AuthState, requestParams: IUpdateRequestParams) => any =
  (auth, requestParams) => {
    return (dispatch: Dispatch<AnyAction>) => {
      dispatch(new FetchingUpdatesAction());

      var newFilters = JSON.parse(JSON.stringify(requestParams.filter.filters));
      if (newFilters.some((f: IFilter) => f.field === "docUpdateDays")) {
        let fromDays = null;
        let toDays = null;
        
        // Single pass to find values and remove docUpdateDays filters
        newFilters = newFilters.filter((f: IFilter) => {
          if (f.field === "docUpdateDays") {
            if (f.operator === "ge") fromDays = parseInt(f.value);
            if (f.operator === "le") toDays = parseInt(f.value);
            return false; // Remove this filter
          }
          return true; // Keep other filters
        });
        
        const isSingleValue = fromDays !== null && toDays !== null && fromDays === toDays;
        const now = moment.utc();
        
        // docUpdateDays >= X means: display value is X or more
        // Documents created X days ago (24-hour periods from now)
        // Upper bound: dateCreated < (now - X days)
        if (fromDays !== null) {
          newFilters.push({
            field: "notification/dateCreated",
            value: now.clone().subtract(fromDays, 'days').toISOString(),
            operator: "lt"
          });
        }
        
        // docUpdateDays <= Y means: display value is Y or less
        // Documents created Y days ago or less (more recent)
        // Lower bound: dateCreated >= (now - (Y+1) days) for range
        //              dateCreated > (now - (Y+1) days) for single value
        if (toDays !== null) {
          newFilters.push({
            field: "notification/dateCreated",
            value: now.clone().subtract(toDays + 1, 'days').toISOString(),
            operator: isSingleValue ? "gt" : "ge"
          });
        }
        
        requestParams.filter.filters = newFilters;
      }

      var newOrderBy = requestParams.orderBy;
      if (newOrderBy.startsWith("docUpdateDays")) {
        var [property, direction] = newOrderBy.split(' ');
        if (property == "docUpdateDays") {
          let newDirection = direction == "asc" ? "desc" : "asc";
          requestParams.orderBy = "notification/dateCreated " + newDirection;
        }
      }

      fetchUpdates(auth, requestParams)
        .then( (response:Api.INotificationsResponse) => {
          dispatch(new FetchedUpdatesAction( {updates: response.notifications} ));
        })
        .catch((errorResponse: Error) => {
          // tslint:disable-next-line:no-console
          console.error(errorResponse);
          dispatch(new FetchUpdatesFailedAction({error: errorResponse.message}));
        });
    }
  }

// This is a thunk action creator for setSortOrder
export const setSortOrder: (orderBy: string) => any = (orderBy) => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch(new SetSortOrderAction({ orderBy }));
  };
};

// This is a thunk action creator for resetSortOrder
export const resetSortOrder: () => any = () => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch(new ResetSortOrderAction());
  };
};

// Filter action creators
export const setFilter = (field: string, value: any, operator?: string) => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch(new SetFilterAction({ field, value, operator }));
  };
};

export const clearFilter = (field: string) => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch(new ClearFilterAction({ field }));
  };
};

export const clearAllFilters = () => {
  return (dispatch: Dispatch<AnyAction>) => {
    dispatch(new ClearAllFiltersAction());
  };
};
